import { RefObject, useCallback } from "react";

type PlaceholderKey = "ct__" | "cn__" | "mn__";

const placeholderFallbackText: Record<PlaceholderKey, string> = {
  ct__: "contact",
  cn__: "client",
  mn__: "matter",
};

const placeholderKeys = Object.keys(placeholderFallbackText) as PlaceholderKey[];
const entityWordPattern = /\b(contact|client|matter)\s*$/i;

const isPlaceholderKey = (value?: string): value is PlaceholderKey => {
  return placeholderKeys.includes(value as PlaceholderKey);
};

const replaceTrailingEntityWord = (text: string, replacement: string) => {
  return text.replace(entityWordPattern, (_match, entity: string) => {
    const resolved = entity.toLowerCase() === replacement ? entity : replacement;
    return `${resolved} `;
  });
};

const appendResolvedPlaceholder = (text: string, placeholder: PlaceholderKey) => {
  const replacement = placeholderFallbackText[placeholder];

  if (entityWordPattern.test(text)) {
    return replaceTrailingEntityWord(text, replacement);
  }

  return `${text}${replacement}`;
};

export const normalizeUnresolvedPlaceholders = (text = "") => {
  let result = "";
  let lastIndex = 0;
  const placeholderRegex = /\b(ct__|cn__|mn__)\b/g;
  let match: RegExpExecArray | null;

  while ((match = placeholderRegex.exec(text)) !== null) {
    result += text.slice(lastIndex, match.index);
    const placeholder = match[1];
    result = isPlaceholderKey(placeholder)
      ? appendResolvedPlaceholder(result, placeholder)
      : result;
    lastIndex = placeholderRegex.lastIndex;
  }

  return result + text.slice(lastIndex);
};

export const serializeGhostTemplateText = (text = "") => {
  return normalizeUnresolvedPlaceholders(text)
    .replace(/\n/g, " ")
    .replace(/\s\s+/g, " ")
    .replace(/[\u200b\u00a0]/g, "")
    .trim();
};

export const useGhostTemplateValueSerializer = (
  editorRef: RefObject<HTMLElement>,
) => {
  const getInternalValue = useCallback(() => {
    if (!editorRef.current) return "";

    let text = "";
    const process = (parentNode: Node) => {
      parentNode.childNodes.forEach((node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          text += node.textContent || "";
          return;
        }

        if (!(node instanceof HTMLElement)) return;

        const placeholder = node.dataset.placeholder;
        if (
          node.dataset.varSpan === "1" &&
          node.dataset.searchAble === "1" &&
          isPlaceholderKey(placeholder)
        ) {
          text = appendResolvedPlaceholder(text, placeholder);
        } else if (node.tagName === "BR") {
          text += "\n";
        } else {
          process(node);
        }
      });
    };

    process(editorRef.current);
    return serializeGhostTemplateText(text);
  }, [editorRef]);

  return { getInternalValue };
};
